import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Users, Phone, Mail, UserCheck, UserX, Download, Search, Filter } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const TeamReport = () => {
  const { user } = useAuth();
  const [teamData, setTeamData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLevel, setFilterLevel] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchTeamReport();
  }, []);

  const fetchTeamReport = async () => {
    try {
      setLoading(true);
      const res = await api.get('/referral/team-report');
      setTeamData(res.data);
    } catch (error) {
      console.error('Failed to fetch team report:', error);
      toast.error('Failed to load team report');
    } finally {
      setLoading(false);
    }
  };

  const filteredMembers = teamData?.members?.filter(member => {
    const matchesSearch = 
      member.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.whatsapp_number?.includes(searchTerm);
    
    const matchesLevel = filterLevel === 'all' || member.level === parseInt(filterLevel);
    const matchesStatus = filterStatus === 'all' || 
      (filterStatus === 'activated' && member.is_activated) ||
      (filterStatus === 'free' && !member.is_activated);
    
    return matchesSearch && matchesLevel && matchesStatus;
  }) || [];

  const exportToCSV = () => {
    if (!filteredMembers.length) {
      toast.error('No data to export');
      return;
    }

    const headers = ['Name', 'Username', 'WhatsApp', 'Email', 'Level', 'Status', 'Joined Date'];
    const rows = filteredMembers.map(m => [
      m.name,
      m.username,
      m.whatsapp_number || '',
      m.email || '',
      m.level,
      m.is_activated ? 'Activated' : 'Free',
      new Date(m.joined_at).toLocaleDateString()
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `team_report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Report exported successfully!');
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="text-xl">Loading team report...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="team-report-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold mb-2">Team Report</h1>
            <p className="text-muted-foreground">Full list of your team members with contact details</p>
          </div>
          <button
            onClick={exportToCSV}
            className="btn-primary flex items-center gap-2"
            data-testid="export-btn"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="glass-card p-6 border-2 border-blue-500/30">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Team</p>
                <p className="text-3xl font-bold text-blue-500" data-testid="total-team">
                  {teamData?.total_team || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="glass-card p-6 border-2 border-green-500/30">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                <UserCheck className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Activated Members</p>
                <p className="text-3xl font-bold text-green-500" data-testid="activated-team">
                  {teamData?.activated_team || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="glass-card p-6 border-2 border-yellow-500/30">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-yellow-500/20 rounded-xl flex items-center justify-center">
                <UserX className="w-6 h-6 text-yellow-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Free Members</p>
                <p className="text-3xl font-bold text-yellow-500" data-testid="free-team">
                  {(teamData?.total_team || 0) - (teamData?.activated_team || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Level Summary */}
        {teamData?.level_summary && Object.keys(teamData.level_summary).length > 0 && (
          <div className="glass-card p-6 mb-8" data-testid="level-summary">
            <h2 className="text-xl font-bold mb-4">Team by Level</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-10 gap-3">
              {Array.from({ length: 10 }, (_, i) => i + 1).map(level => {
                const data = teamData.level_summary[level] || { total: 0, activated: 0 };
                return (
                  <div key={level} className="bg-white/5 rounded-xl p-3 text-center">
                    <p className="text-xs text-muted-foreground">L{level}</p>
                    <p className="text-xl font-bold text-primary">{data.total}</p>
                    <p className="text-xs text-green-500">{data.activated} active</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="glass-card p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search by name, username, or mobile..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-white/10 border border-white/20 rounded-lg pl-10 pr-4 py-3"
                data-testid="search-input"
              />
            </div>
            
            {/* Level Filter */}
            <select
              value={filterLevel}
              onChange={(e) => setFilterLevel(e.target.value)}
              className="bg-white/10 border border-white/20 rounded-lg px-4 py-3"
              data-testid="level-filter"
            >
              <option value="all">All Levels</option>
              {Array.from({ length: 10 }, (_, i) => i + 1).map(level => (
                <option key={level} value={level}>Level {level}</option>
              ))}
            </select>
            
            {/* Status Filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="bg-white/10 border border-white/20 rounded-lg px-4 py-3"
              data-testid="status-filter"
            >
              <option value="all">All Status</option>
              <option value="activated">Activated Only</option>
              <option value="free">Free Only</option>
            </select>
          </div>
        </div>

        {/* Team Table */}
        <div className="glass-card overflow-hidden" data-testid="team-table">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white/10">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Name</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Username</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">WhatsApp</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Email</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold">Level</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Joined</th>
                </tr>
              </thead>
              <tbody>
                {filteredMembers.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-12 text-center text-muted-foreground">
                      {searchTerm || filterLevel !== 'all' || filterStatus !== 'all' 
                        ? 'No members match your filters' 
                        : 'No team members yet. Share your referral link!'}
                    </td>
                  </tr>
                ) : (
                  filteredMembers.map((member, idx) => (
                    <tr 
                      key={idx} 
                      className="border-t border-white/5 hover:bg-white/5"
                      data-testid={`team-member-${idx}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            member.is_activated ? 'bg-green-500/20 text-green-500' : 'bg-white/10'
                          }`}>
                            {member.name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <span className="font-semibold">{member.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">@{member.username}</td>
                      <td className="px-4 py-3">
                        {member.whatsapp_number ? (
                          <a 
                            href={`https://wa.me/${member.whatsapp_number}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-[#25D366] hover:underline"
                          >
                            <Phone className="w-4 h-4" />
                            {member.whatsapp_number}
                          </a>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {member.email ? (
                          <a 
                            href={`mailto:${member.email}`}
                            className="flex items-center gap-1 text-primary hover:underline text-sm"
                          >
                            <Mail className="w-4 h-4" />
                            {member.email}
                          </a>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="px-3 py-1 bg-primary/20 text-primary rounded-full text-sm font-bold">
                          L{member.level}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          member.is_activated 
                            ? 'bg-green-500/20 text-green-500' 
                            : 'bg-yellow-500/20 text-yellow-500'
                        }`}>
                          {member.is_activated ? 'Activated' : 'Free'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {new Date(member.joined_at).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {filteredMembers.length > 0 && (
            <div className="p-4 bg-white/5 text-sm text-muted-foreground">
              Showing {filteredMembers.length} of {teamData?.total_team || 0} members
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamReport;
